import { createServerFn } from "@tanstack/react-start"
import { z } from "zod"

import { getUserType, resolveAuthContext } from "@/lib/supabase/auth"
import { createClient } from "@/lib/supabase/server"
import { normalizePhoneNumberForSupabase } from "@/lib/utils"
import type { UserType } from "@/routes/__root"

export function isTerminalAuthorized(userType: UserType): boolean {
  return userType === "terminal" || userType === "admin"
}

export const getTerminalAuthContext = createServerFn({
  method: "GET",
}).handler(async () => {
  return resolveAuthContext()
})

export const verifyTerminalOtp = createServerFn({ method: "POST" })
  .inputValidator((data: unknown) =>
    z.object({ phone: z.string().min(1), token: z.string().min(1) }).parse(data)
  )
  .handler(async ({ data }) => {
    const supabase = createClient()
    const {
      data: { user },
      error,
    } = await supabase.auth.verifyOtp({
      phone: `+${normalizePhoneNumberForSupabase(data.phone)}`,
      token: data.token,
      type: "sms",
    })
    if (error) {
      return { hasError: true, message: error.message }
    }
    if (!user) {
      return { hasError: true, message: "Authentication failed" }
    }
    const userType = await getUserType(user)
    if (!isTerminalAuthorized(userType)) {
      await supabase.auth.signOut()
      return {
        hasError: true,
        message: "You are not authorized to access the terminal",
      }
    }
    return { hasError: false, redirectTo: "/terminal" }
  })

export const exchangeCodeForSession = createServerFn({ method: "POST" })
  .inputValidator((data: unknown) =>
    z.object({ code: z.string().min(1) }).parse(data)
  )
  .handler(async ({ data }) => {
    const supabase = createClient()
    const {
      data: { user },
      error,
    } = await supabase.auth.exchangeCodeForSession(data.code)
    if (error || !user) {
      return { hasError: true, errorType: "invalid_code" as const }
    }
    const userType = await getUserType(user)
    if (!isTerminalAuthorized(userType)) {
      await supabase.auth.signOut()
      return { hasError: true, errorType: "unauthorized" as const }
    }
    return { hasError: false }
  })

export const terminalLogout = createServerFn({ method: "POST" }).handler(
  async () => {
    const supabase = createClient()
    await supabase.auth.signOut()
  }
)
